/**
 * Channel detail fallback catalog — Bill prototype 2026-05-30.
 *
 * The /channels/[key] route's primary data source is channel_catalog +
 * subchannel_catalog via getChannelCatalog(). When that returns null for
 * the requested key (anon visitor + RLS not applied, schema migration
 * not yet on the preview DB, etc) we render the prototype's hardcoded
 * channel + subchannel list instead of 404-ing. Once the catalog tables
 * are populated on every environment this file becomes dead code; until
 * then it keeps /channels/{search,social,tv,programmatic} renderable
 * from a cold preview database.
 *
 * Subchannel `key` values mirror what subchannel_catalog uses (slug-case)
 * so the CONNECTOR_LOGO map in ChannelDetailPage.tsx keys cleanly.
 */

import type {
  CatalogChannelView,
  CatalogSubchannelView,
} from '@/lib/connectors/get-catalog'

interface FallbackSub {
  key: string
  name: string
  description: string
}

function sub(channelKey: string, partial: FallbackSub): CatalogSubchannelView {
  return {
    key: partial.key,
    channel_key: channelKey,
    name: partial.name,
    description: partial.description,
    status: 'available',
    accent_hex: null,
    display_order: 0,
    enabled: true,
    auth_mode: 'user_oauth',
    icon_hint: null,
    connection: null,
  } as unknown as CatalogSubchannelView
}

function channel(
  key: string,
  name: string,
  description: string,
  accentHex: string,
  displayOrder: number,
  subs: CatalogSubchannelView[],
): CatalogChannelView {
  return {
    key,
    name,
    description,
    accent_hex: accentHex,
    display_order: displayOrder,
    enabled: true,
    subchannels: subs,
  } as unknown as CatalogChannelView
}

export const FALLBACK_CHANNELS: Record<string, CatalogChannelView> = {
  search: channel(
    'search',
    'Search',
    'Intent-driven demand capture across search engines and marketplaces.',
    '#A8780C',
    1,
    [
      sub('search', { key: 'google-search', name: 'Google Ads', description: 'Search, Performance Max, Shopping, YouTube' }),
      sub('search', { key: 'microsoft-ads', name: 'Microsoft Ads', description: 'Bing search + Microsoft Audience Network' }),
      sub('search', { key: 'apple-search', name: 'Apple Search', description: 'App Store search ads' }),
      sub('search', { key: 'yahoo-ads', name: 'Yahoo Ads', description: 'Yahoo + AOL search inventory' }),
      sub('search', { key: 'duckduckgo', name: 'DuckDuckGo Ads', description: 'Privacy-first search (via Bing)' }),
      sub('search', { key: 'amazon-search', name: 'Amazon Search', description: 'Sponsored Products + Brands' }),
    ],
  ),
  social: channel(
    'social',
    'Social',
    'Paid social across feeds, stories, and creator placements.',
    '#2D6CDF',
    2,
    [
      sub('social', { key: 'meta', name: 'Meta', description: 'Facebook + Instagram + Messenger + Audience Network' }),
      sub('social', { key: 'pinterest', name: 'Pinterest', description: 'Idea pins, shopping ads, promoted pins' }),
      sub('social', { key: 'linkedin', name: 'LinkedIn', description: 'Sponsored content, message ads, lead gen' }),
      sub('social', { key: 'tiktok', name: 'TikTok', description: 'In-feed, spark ads, TopView' }),
      sub('social', { key: 'reddit', name: 'Reddit', description: 'Promoted posts + community targeting' }),
      sub('social', { key: 'snapchat', name: 'Snapchat', description: 'Snap ads + AR lenses + filters' }),
      sub('social', { key: 'x-twitter', name: 'X (Twitter)', description: 'Promoted posts, takeovers, amplify' }),
      sub('social', { key: 'youtube-shorts', name: 'YouTube Shorts', description: 'Shorts-specific placement' }),
      sub('social', { key: 'nextdoor', name: 'Nextdoor', description: 'Hyperlocal neighborhood targeting' }),
    ],
  ),
  tv: channel(
    'tv',
    'TV / CTV',
    'Premium streaming and linear TV — the living-room screen.',
    '#7A5BD6',
    3,
    [
      sub('tv', { key: 'roku', name: 'Roku', description: 'OneView SSP — Roku + The Roku Channel' }),
      sub('tv', { key: 'nbc-universal', name: 'NBC Universal', description: 'NBC, Bravo, USA, Telemundo' }),
      sub('tv', { key: 'peacock', name: 'Peacock', description: 'NBCU streaming, premium AVOD' }),
      sub('tv', { key: 'hulu', name: 'Hulu', description: 'Disney Ad Sales — Hulu + Live TV' }),
      sub('tv', { key: 'paramount-plus', name: 'Paramount+', description: 'Paramount streaming + CBS' }),
      sub('tv', { key: 'fubotv', name: 'fuboTV', description: 'Sports-first vMVPD' }),
      sub('tv', { key: 'disney-plus', name: 'Disney+', description: 'Disney+ ad tier' }),
      sub('tv', { key: 'max', name: 'Max', description: 'WBD — Max ad-supported' }),
      sub('tv', { key: 'samsung-tv-plus', name: 'Samsung TV Plus', description: 'Samsung FAST channels' }),
      sub('tv', { key: 'lg-channels', name: 'LG Channels', description: 'LG smart TV FAST' }),
      sub('tv', { key: 'amazon-prime-video', name: 'Amazon Prime Video', description: 'Prime Video ad tier + Freevee' }),
      sub('tv', { key: 'pluto-tv', name: 'Pluto TV', description: 'Paramount FAST service' }),
      sub('tv', { key: 'tubi', name: 'Tubi', description: 'Fox AVOD' }),
      sub('tv', { key: 'sling-tv', name: 'Sling TV', description: 'Dish vMVPD' }),
      sub('tv', { key: 'vizio', name: 'Vizio (Inscape)', description: 'ACR-powered Vizio inventory' }),
      sub('tv', { key: 'linear-broadcast', name: 'Linear (broadcast)', description: 'Tegna, Sinclair, Nexstar, Gray, Hearst' }),
    ],
  ),
  programmatic: channel(
    'programmatic',
    'Programmatic',
    'Open-web display, video, and CTV bought across DSPs.',
    '#0A8A6E',
    4,
    [
      sub('programmatic', { key: 'the-trade-desk', name: 'The Trade Desk', description: 'Independent DSP — display, video, audio, CTV' }),
      sub('programmatic', { key: 'sonobi', name: 'Sonobi', description: 'JetStream — direct publisher relationships' }),
      sub('programmatic', { key: 'dv360', name: 'DV360', description: 'Google Display & Video 360' }),
      sub('programmatic', { key: 'magnite', name: 'Magnite', description: 'Independent SSP — CTV + display' }),
      sub('programmatic', { key: 'xandr', name: 'Xandr', description: 'Microsoft Curate + Invest' }),
      sub('programmatic', { key: 'criteo', name: 'Criteo', description: 'Commerce media + retargeting' }),
      sub('programmatic', { key: 'adobe-advertising', name: 'Adobe Advertising', description: 'Adobe DSP (formerly TubeMogul)' }),
      sub('programmatic', { key: 'amazon-dsp', name: 'Amazon DSP', description: 'On/off Amazon programmatic' }),
      sub('programmatic', { key: 'stackadapt', name: 'StackAdapt', description: 'Self-serve — display, native, CTV' }),
      sub('programmatic', { key: 'basis-centro', name: 'Basis (Centro)', description: 'Omnichannel DSP for agencies' }),
      sub('programmatic', { key: 'mediamath', name: 'MediaMath', description: 'Programmatic — display + video' }),
      sub('programmatic', { key: 'pubmatic', name: 'PubMatic', description: 'Independent SSP' }),
    ],
  ),
  audio: channel(
    'audio',
    'Audio',
    'Streaming and broadcast audio — Spotify, Pandora, iHeartRadio, SiriusXM, Audacy.',
    '#E0762D',
    5,
    [
      sub('audio', { key: 'spotify-ads', name: 'Spotify Ads', description: 'Audio + video + sponsored playlist' }),
      sub('audio', { key: 'pandora', name: 'Pandora', description: 'SiriusXM Media' }),
      sub('audio', { key: 'iheartradio', name: 'iHeartRadio', description: 'Broadcast + streaming radio' }),
      sub('audio', { key: 'siriusxm', name: 'SiriusXM', description: 'Satellite + streaming audio' }),
      sub('audio', { key: 'audacy', name: 'Audacy', description: 'Podcast + radio network' }),
    ],
  ),
  retail: channel(
    'retail',
    'Retail Media',
    'Retail media networks — Amazon, Walmart, Target, Instacart, Kroger.',
    '#C2185B',
    6,
    [
      sub('retail', { key: 'amazon-ads', name: 'Amazon Ads', description: 'Sponsored Products, Brands, Display' }),
      sub('retail', { key: 'walmart-connect', name: 'Walmart Connect', description: 'Onsite + offsite retail media' }),
      sub('retail', { key: 'roundel-target', name: 'Roundel (Target)', description: 'Target retail media network' }),
      sub('retail', { key: 'instacart-ads', name: 'Instacart Ads', description: 'Sponsored grocery placement' }),
      sub('retail', { key: 'kroger-precision', name: 'Kroger Precision', description: '84.51° — Kroger retail media' }),
    ],
  ),
}
